// Pure, testable helpers for the desktop notifications feed + preferences.
// Consumes the shared backend contract EXACTLY:
//   GET  /notifications?cursor&limit        -> {items,next_cursor,has_more}
//   GET  /notifications/unread-count         -> {unread}
//   POST /notifications/{nid}/read           -> {changed}
//   POST /notifications/read-all             -> {ok,changed}
//   GET  /notifications/preferences          -> {categories,muted_teams,muted_rooms,enum:{categories}}
//   PUT  /notifications/preferences {categories,muted_teams,muted_rooms}
// Every field is read defensively so an unknown/missing backend field fails
// safely (no crash, no fabricated state).

// Honest display labels for the documented category enum. An unknown category
// (new backend value) falls back to a humanized form — never crashes.
export const CATEGORY_LABELS = {
  team_kickoff: "Team kickoff",
  game_live: "Game is live",
  dm_reply: "Direct message",
  room_invite: "Room invite",
  rivalry_invite: "Rivalry invite",
  pick_deadline: "Pick deadline",
  prediction_graded: "Prediction graded",
  matchup_result: "Matchup result",
  award: "Award",
  close_game: "Close game",
};
export const CATEGORY_DESCRIPTIONS = {
  team_kickoff: "A team you follow is about to kick off.",
  game_live: "A game you care about just went live.",
  dm_reply: "Someone replied to your direct message.",
  room_invite: "You were invited to a room.",
  rivalry_invite: "You were challenged to a rivalry.",
  pick_deadline: "A pick deadline is approaching.",
  prediction_graded: "Your prediction was graded.",
  matchup_result: "A matchup you followed has a result.",
  award: "You earned an award.",
  close_game: "A game you're watching is coming down to the wire.",
};

export function humanize(key) {
  return String(key || "").replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).trim();
}
export function categoryLabel(cat) {
  if (!cat) return "Notification";
  return CATEGORY_LABELS[cat] || humanize(cat);
}

function firstDefined(...vals) {
  for (const v of vals) if (v !== undefined && v !== null && v !== "") return v;
  return null;
}

// Build a web-navigable link for a notification. Only relative in-app paths are
// returned (a mobile deep link like turftalk://… is ignored). Derived from an
// explicit relative link, else from common id fields in the item/metadata.
export function deriveWebLink(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const explicit = firstDefined(r.web_url, r.web_link, r.link, r.deep_link, r.url, r.target);
  if (typeof explicit === "string" && explicit.startsWith("/")) return explicit;
  const meta = (r.data && typeof r.data === "object" ? r.data : null) || (r.metadata && typeof r.metadata === "object" ? r.metadata : null) || r;
  const gid = firstDefined(meta.game_id, meta.gameId);
  if (gid) return `/game/${gid}`;
  const rid = firstDefined(meta.room_id, meta.roomId);
  if (rid) return `/room/${rid}`;
  const pid = firstDefined(meta.player_id, meta.playerId);
  if (pid) return `/player/${pid}`;
  const tid = firstDefined(meta.team_id, meta.teamId);
  if (tid) return `/team/${tid}`;
  return null;
}

// Normalize one feed item into a stable render shape. Tolerant of field-name
// variants across backend revisions.
export function normalizeNotification(raw) {
  const r = raw && typeof raw === "object" ? raw : {};
  const id = firstDefined(r.id, r._id, r.notification_id, r.nid);
  const category = firstDefined(r.category, r.type, r.kind) || null;
  const read = r.read === true || r.is_read === true || r.seen === true || !!firstDefined(r.read_at, r.seen_at);
  return {
    id: id != null ? String(id) : null,
    category,
    label: categoryLabel(category),
    title: firstDefined(r.title, r.headline, r.subject) || categoryLabel(category),
    body: firstDefined(r.body, r.message, r.text, r.description, r.summary) || "",
    createdAt: firstDefined(r.created_at, r.createdAt, r.ts, r.timestamp, r.time),
    read,
    link: deriveWebLink(r),
    raw: r,
  };
}

export function normalizeFeed(resp) {
  const r = resp && typeof resp === "object" ? resp : {};
  const rawItems = Array.isArray(r.items) ? r.items : (Array.isArray(r.notifications) ? r.notifications : (Array.isArray(r) ? r : []));
  const items = rawItems.map(normalizeNotification).filter((n) => n.id != null);
  return {
    items,
    nextCursor: firstDefined(r.next_cursor, r.nextCursor, r.cursor),
    hasMore: r.has_more === true || r.hasMore === true || (!!firstDefined(r.next_cursor, r.nextCursor) && r.has_more !== false),
  };
}

export function unreadFromResponse(resp) {
  const r = resp && typeof resp === "object" ? resp : {};
  const n = Number(firstDefined(r.unread, r.unread_count, r.count, r.total) ?? 0);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

// Format an unread count for a compact badge (caps at 99+).
export function formatBadge(count) {
  const n = Number(count) || 0;
  if (n <= 0) return null;
  return n > 99 ? "99+" : String(n);
}

// Normalize the preferences payload. The category toggle set is driven by the
// backend-reported enum (source of truth); each enum key resolves to its
// current boolean (default true when the backend omits it, matching observed
// defaults — but any explicit false is honored).
export function normalizePreferences(resp) {
  const r = resp && typeof resp === "object" ? resp : {};
  const categories = r.categories && typeof r.categories === "object" ? r.categories : {};
  const enumCats = Array.isArray(r.enum?.categories)
    ? r.enum.categories
    : (Array.isArray(r.category_enum) ? r.category_enum : Object.keys(categories));
  const items = enumCats.map((key) => ({
    key,
    label: categoryLabel(key),
    description: CATEGORY_DESCRIPTIONS[key] || "",
    enabled: categories[key] !== false,
  }));
  return {
    categories,
    items,
    enum: enumCats,
    mutedTeams: Array.isArray(r.muted_teams) ? r.muted_teams : [],
    mutedRooms: Array.isArray(r.muted_rooms) ? r.muted_rooms : [],
  };
}

// Build the PUT body to flip ONE category while preserving the rest. Sends the
// full categories map (backend also merges partials, but a full map is safest).
export function buildPreferenceUpdate(prefs, key, value) {
  const base = prefs && typeof prefs.categories === "object" ? prefs.categories : {};
  return { categories: { ...base, [key]: value } };
}
